import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import api from '../../api/axios';
import { Continent, Country } from './types';

const GeographySection: React.FC = () => {
  const [continents, setContinents] = useState<Continent[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [form, setForm] = useState({ code: '', name: '', continentId: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [continentResponse, countryResponse] = await Promise.all([api.get('/sales-admin/continents'), api.get('/sales-admin/countries')]);
      setContinents(continentResponse.data); setCountries(countryResponse.data);
    } catch (err: any) { setError(err?.response?.data?.message || 'Coğrafi bilgiler yüklenemedi.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createCountry = async () => {
    setSaving(true); setError(null);
    try { await api.post('/sales-admin/countries', form); setForm({ code: '', name: '', continentId: '' }); await load(); }
    catch (err: any) { setError(err?.response?.data?.message || 'Ülke eklenemedi.'); }
    finally { setSaving(false); }
  };
  const changeContinent = async (countryId: string, continentId: string) => {
    try { await api.patch(`/sales-admin/countries/${countryId}`, { continentId }); await load(); }
    catch (err: any) { setError(err?.response?.data?.message || 'Ülkenin kıtası güncellenemedi.'); }
  };

  return <Stack spacing={3}>
    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
    <Box><Typography variant="h4" fontWeight={700}>Ülkeler ve Kıtalar</Typography><Typography color="text.secondary">Ülkeleri kıtalara bağlayın; kıta yetkileri bu eşleşmeye göre çalışır.</Typography></Box>
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Yeni Ülke</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField label="Ülke Kodu" inputProps={{ maxLength: 2 }} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        <TextField label="Ülke Adı" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ flex: 1 }} />
        <FormControl sx={{ minWidth: 220 }}><InputLabel>Kıta / Bölge</InputLabel><Select label="Kıta / Bölge" value={form.continentId} onChange={(e) => setForm({ ...form, continentId: e.target.value })}>{continents.map((continent) => <MenuItem key={continent.id} value={continent.id}>{continent.name}</MenuItem>)}</Select></FormControl>
        <Button variant="contained" onClick={createCountry} disabled={saving || form.code.length !== 2 || !form.name.trim() || !form.continentId}>Ekle</Button>
      </Stack>
    </Paper>
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : <Table><TableHead><TableRow><TableCell>Kod</TableCell><TableCell>Ülke</TableCell><TableCell>Kıta / Bölge</TableCell></TableRow></TableHead><TableBody>{countries.map((country) => <TableRow key={country.id}><TableCell>{country.code}</TableCell><TableCell>{country.name}</TableCell><TableCell><Select size="small" value={country.continentId || ''} displayEmpty onChange={(e) => changeContinent(country.id, e.target.value)} sx={{ minWidth: 220 }}><MenuItem value="" disabled>Kıta seçin</MenuItem>{continents.map((continent) => <MenuItem key={continent.id} value={continent.id}>{continent.name}</MenuItem>)}</Select></TableCell></TableRow>)}</TableBody></Table>}
    </TableContainer>
  </Stack>;
};

export default GeographySection;
